import type { JSX } from "react";
import { m, useReducedMotion } from "motion/react";
import { entrance } from "../ds/core/motion";
import {
  Check, MapPin, Clock, FileCheck, Truck, Receipt, Coffee, X, Navigation,
  Palmtree, FileText,
} from "lucide-react";
import { Sparkline, ShieldBadge, Donut } from "../ds/core";
import { useLocale } from "../i18n/useLocale";

/**
 * FeatureVisuals — a set of mini, animated illustrations for feature cards (the
 * "What's inside Time" bento principle). One visual per feature TYPE; `visualFor`
 * maps a feature title to exactly one of them (specific categories first, so two
 * different features never share an animation). Add a type → add a visual + rule.
 */

const EASE = [0.22, 1, 0.36, 1] as const;
const PANEL = "relative mt-auto overflow-hidden rounded-xl border border-line bg-white";
const MAP_GRID = {
  backgroundImage:
    "linear-gradient(to right, rgba(29,33,56,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(29,33,56,0.05) 1px, transparent 1px)",
  backgroundSize: "22px 22px",
} as const;

/* 1 ── Location-verified check-in — map with pulsing geo pins. */
function CheckinVisual() {
  const pins = [{ l: "26%", t: "58%", ok: true }, { l: "54%", t: "34%", ok: true }, { l: "80%", t: "62%", ok: false }];
  return (
    <div className={`${PANEL} h-[120px]`}>
      <div className="absolute inset-0" style={MAP_GRID} />
      {pins.map((p, i) => (
        <div key={i} className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: p.l, top: p.t }}>
          <span className="relative flex h-6 w-6">
            {p.ok && <span className="ds-locate absolute inline-flex h-full w-full rounded-full bg-ds-pos" style={{ animationDelay: `${i * 0.5}s` }} />}
            <span className={`relative grid h-6 w-6 place-items-center rounded-full text-white ring-2 ring-white ${p.ok ? "bg-ds-pos" : "bg-red-500"}`}>
              {p.ok ? <Check size={12} strokeWidth={3} /> : <MapPin size={11} strokeWidth={3} />}
            </span>
          </span>
        </div>
      ))}
    </div>
  );
}

/* 2 ── Live hours / budget — an animated progress bar + hours chip. */
function HoursVisual() {
  const { t } = useLocale();
  return (
    <div className={`${PANEL} h-[120px] p-3.5`}>
      <div className="flex items-center justify-between text-[12px]">
        <span className="text-ds-ink-soft">{t("features:viz.hours.remaining")}</span>
        <span className="font-semibold text-ds-ink">62%</span>
      </div>
      <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-bg-subtle">
        <m.div className="h-full rounded-full bg-ds-accent" initial={entrance({ width: 0 })} animate={{ width: "62%" }} transition={{ duration: 1, ease: EASE, delay: 0.2 }} />
      </div>
      <div className="mt-3 flex items-center gap-2 rounded-lg bg-bg-subtle px-2.5 py-2">
        <span className="grid h-6 w-6 place-items-center rounded-md bg-white text-ds-accent"><Clock size={12} /></span>
        <span className="flex-1 text-[11px] text-ds-ink-soft">{t("features:viz.hours.live")}</span>
        <span className="text-[11px] font-bold text-ds-ink">328h</span>
      </div>
    </div>
  );
}

/* 3 ── Payroll export — rows that tick green in sequence. */
function PayrollVisual() {
  const rows = ["Albert E.", "Julia R.", "Crew B"];
  return (
    <div className={`${PANEL} flex h-[120px] flex-col justify-center gap-1.5 p-3`}>
      {rows.map((r, i) => (
        <m.div key={r} initial={entrance({ opacity: 0, x: -6 })} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.18, duration: 0.4, ease: EASE }}
          className="flex items-center gap-2 rounded-lg bg-bg-subtle px-2.5 py-1.5">
          <span className="grid h-5 w-5 place-items-center rounded-md bg-white text-ds-accent"><FileCheck size={11} /></span>
          <span className="flex-1 text-[11px] font-medium text-ds-ink">{r}</span>
          <span className="text-[10px] text-ds-ink-soft">{[164, 152, 188][i]}h</span>
          <span className="grid h-4 w-4 place-items-center rounded-full bg-ds-pos text-white"><Check size={9} strokeWidth={3.5} /></span>
        </m.div>
      ))}
    </div>
  );
}

/* 4 ── Team / subcontractors — overlapping presence avatars. */
function TeamVisual() {
  const { t } = useLocale();
  const subs = ["D", "V", "P", "S"];
  return (
    <div className={`${PANEL} flex h-[120px] flex-col justify-center gap-3 p-3.5`}>
      <div className="flex items-center -space-x-2">
        {subs.map((s, i) => (
          <m.span key={s} initial={entrance({ opacity: 0, scale: 0.6 })} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.15 + i * 0.12, duration: 0.35, ease: EASE }}
            className="grid h-9 w-9 place-items-center rounded-full bg-ds-accent text-[12px] font-bold text-white ring-2 ring-white">{s}</m.span>
        ))}
        <span className="grid h-9 w-9 place-items-center rounded-full bg-bg-subtle text-[11px] font-bold text-ds-ink-soft ring-2 ring-white">+6</span>
      </div>
      <p className="text-[11px] text-ds-ink-soft">{t("features:viz.team.status")}</p>
    </div>
  );
}

/* 5 ── Mileage / transport — a route sparkline. */
function RouteVisual() {
  const { t } = useLocale();
  return (
    <div className={`${PANEL} h-[120px] p-3.5`}>
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 text-[12px] text-ds-ink-soft"><Truck size={14} className="text-ds-accent" /> {t("features:viz.route.trips")}</span>
        <span className="text-[12px] font-semibold text-ds-ink">+€500</span>
      </div>
      <Sparkline className="mt-2" data={[16, 22, 19, 30, 26, 38, 33, 48]} highlight={6} />
    </div>
  );
}

/* 6 ── Compliance / proof — the dimensional shield. */
function ComplianceVisual() {
  return (
    <div className={`${PANEL} grid h-[120px] place-items-center`}>
      <div className="scale-90"><ShieldBadge /></div>
    </div>
  );
}

/* 7 ── Expenses / extra work & materials — receipts logged on site. */
function ExpenseVisual() {
  const { t } = useLocale();
  const rows = [{ l: t("features:viz.expense.fuel"), v: "€42" }, { l: t("features:viz.expense.parts"), v: "€18" }, { l: t("features:viz.expense.photo"), v: "" }];
  return (
    <div className={`${PANEL} flex h-[120px] flex-col justify-center gap-1.5 p-3`}>
      {rows.map((r, i) => (
        <m.div key={r.l} initial={entrance({ opacity: 0, y: 6 })} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 + i * 0.16, duration: 0.4, ease: EASE }}
          className="flex items-center gap-2 rounded-lg bg-bg-subtle px-2.5 py-1.5">
          <span className="grid h-5 w-5 place-items-center rounded-md bg-white text-ds-accent"><Receipt size={11} /></span>
          <span className="flex-1 text-[11px] font-medium text-ds-ink">{r.l}</span>
          {r.v && <span className="text-[11px] font-bold text-ds-ink">{r.v}</span>}
        </m.div>
      ))}
    </div>
  );
}

/* 8 ── Absence & sick notes — a week with a flagged sick day. */
function AbsenceVisual() {
  const { t } = useLocale();
  return (
    <div className={`${PANEL} h-[120px] p-3.5`}>
      <div className="flex items-center justify-between text-[12px]">
        <span className="text-ds-ink-soft">{t("features:viz.absence.week")}</span>
        <span className="font-semibold text-ds-ink">{t("features:viz.absence.count")}</span>
      </div>
      <div className="mt-3 grid grid-cols-5 gap-1.5">
        {[t("features:viz.day.mon"), t("features:viz.day.tue"), t("features:viz.day.wed"), t("features:viz.day.thu"), t("features:viz.day.fri")].map((d, i) => (
          <m.div key={i} initial={entrance({ opacity: 0, y: 5 })} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.08, duration: 0.3 }}
            className={`flex flex-col items-center gap-1 rounded-lg py-2 ${i === 2 ? "bg-ds-warn/15" : "bg-bg-subtle"}`}>
            <span className="text-[9px] text-ds-ink-soft">{d}</span>
            <span className={`grid h-4 w-4 place-items-center rounded-full text-white ${i === 2 ? "bg-ds-warn" : "bg-ds-pos"}`}>
              {i === 2 ? <X size={9} strokeWidth={3.5} /> : <Check size={9} strokeWidth={3.5} />}
            </span>
          </m.div>
        ))}
      </div>
    </div>
  );
}

/* 9 ── Overtime — the register: each row's compensation is Free time or Paid
   (corrections web.pdf p.12). That per-row choice is the feature. */
const OT_ROWS = [
  { paid: false, project: "Kitchen renovation", dur: "3h 0m" },
  { paid: true, project: "Warehouse — Aalst", dur: "2h 30m" },
  { paid: false, project: "Office fit-out", dur: "30m" },
];
function OvertimeVisual() {
  const { t } = useLocale();
  return (
    <div className={`${PANEL} h-[120px] p-2`}>
      {OT_ROWS.map((r, i) => (
        <m.div key={i} initial={entrance({ opacity: 0, y: 5 })} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.1, duration: 0.4, ease: EASE }}
          className="grid grid-cols-[74px_1fr_auto] items-center gap-2 border-b border-line px-1 py-2 last:border-0">
          <span className={`inline-block w-fit rounded-full px-2 py-0.5 text-[10px] font-semibold text-white ${r.paid ? "bg-ds-warn" : "bg-ds-accent"}`}>
            {r.paid ? t("features:viz.overtime.paid") : t("features:viz.overtime.freeTime")}
          </span>
          <span className="truncate text-[11px] text-ds-ink-soft">{r.project}</span>
          <span className="text-[11px] font-semibold text-ds-ink">{r.dur}</span>
        </m.div>
      ))}
    </div>
  );
}

/* 10 ── Automatic break deduction — coffee + auto-subtract. */
function BreakVisual() {
  const { t } = useLocale();
  return (
    <div className={`${PANEL} flex h-[120px] flex-col justify-center gap-3 p-3.5`}>
      <div className="flex items-center gap-2.5">
        <span className="grid h-9 w-9 place-items-center rounded-lg bg-bg-subtle text-ds-accent"><Coffee size={16} /></span>
        <span className="flex-1 text-[12px] font-medium text-ds-ink">{t("features:viz.break.label")}</span>
        <span className="rounded-full bg-ds-pos/15 px-2 py-0.5 text-[11px] font-semibold text-ds-pos">{t("features:viz.break.badge")}</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-bg-subtle">
        <m.div className="h-full rounded-full bg-ds-accent" initial={entrance({ width: 0 })} animate={{ width: "70%" }} transition={{ duration: 1, ease: EASE, delay: 0.2 }} />
      </div>
    </div>
  );
}

/* 11 ── Shift scheduling — a week roster grid. */
function ScheduleVisual() {
  const { t } = useLocale();
  const days = [t("features:viz.day.mon"), t("features:viz.day.tue"), t("features:viz.day.wed"), t("features:viz.day.thu"), t("features:viz.day.fri"), t("features:viz.day.sat"), t("features:viz.day.sun")];
  const shifts = [[1, 0], [1, 1], [0, 1], [1, 0], [1, 1], [0, 0], [0, 0]];
  return (
    <div className={`${PANEL} grid h-[120px] place-items-center p-3`}>
      <div className="grid w-full grid-cols-7 gap-1.5">
        {shifts.map((col, i) => (
          <div key={i} className="flex flex-col items-center gap-1">
            <span className="text-[8px] text-ds-ink-soft">{days[i]}</span>
            {col.map((on, r) => (
              <m.div key={r} initial={entrance({ opacity: 0, scaleY: 0.4 })} animate={{ opacity: 1, scaleY: 1 }} transition={{ delay: 0.1 + (i * 2 + r) * 0.04, duration: 0.3 }}
                className={`h-4 w-full rounded ${on ? "bg-ds-accent" : "bg-bg-subtle"}`} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/* 12 ── Navigation to site — a route to a pin with ETA. */
function NavigationVisual() {
  return (
    <div className={`${PANEL} h-[120px]`}>
      <div className="absolute inset-0" style={MAP_GRID} />
      <svg viewBox="0 0 240 120" preserveAspectRatio="none" className="absolute inset-0 h-full w-full" fill="none" aria-hidden>
        <path d="M20 100 C 70 95 80 40 130 50 S 200 40 220 28" stroke="var(--ds-accent)" strokeWidth="3" strokeLinecap="round" strokeDasharray="6 6" />
      </svg>
      <span className="absolute bottom-3 left-3 grid h-7 w-7 place-items-center rounded-full bg-ds-ink text-white ring-2 ring-white"><Navigation size={13} strokeWidth={2.4} /></span>
      <div className="absolute right-3 top-4 -translate-y-1/2">
        <span className="relative flex h-6 w-6">
          <span className="ds-locate absolute inline-flex h-full w-full rounded-full bg-ds-accent" />
          <span className="relative grid h-6 w-6 place-items-center rounded-full bg-ds-accent text-white ring-2 ring-white"><MapPin size={11} strokeWidth={3} /></span>
        </span>
      </div>
      <span className="absolute right-3 bottom-3 rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-ds-ink shadow-sm">12 min</span>
    </div>
  );
}

/* 13 ── Cost control / margin — a cost-split donut. */
function CostVisual() {
  const { t } = useLocale();
  return (
    <div className={`${PANEL} grid h-[120px] place-items-center`}>
      <div className="flex items-center gap-4">
        <Donut size={84} stroke={14} gap={4} rim={2}
          segments={[{ value: 47, color: "var(--ds-accent)" }, { value: 25, color: "var(--ds-peach)" }, { value: 28, color: "var(--ds-mint)" }]}
          center={<span className="text-[12px] font-bold text-ds-ink">23%</span>} />
        <div className="flex flex-col gap-1.5 text-[11px] text-ds-ink-soft">
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: "var(--ds-accent)" }} /> {t("features:viz.cost.labour")}</span>
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: "var(--ds-peach)" }} /> {t("features:viz.cost.materials")}</span>
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: "var(--ds-mint)" }} /> {t("features:viz.cost.margin")}</span>
        </div>
      </div>
    </div>
  );
}

/* 14 ── Leave requests & balances — an approved request + a balance bar. */
function LeaveVisual() {
  const { t } = useLocale();
  return (
    <div className={`${PANEL} h-[120px] p-3.5`}>
      <div className="flex items-center gap-2.5">
        <span className="grid h-8 w-8 place-items-center rounded-lg bg-bg-subtle text-ds-accent"><Palmtree size={15} /></span>
        <span className="flex-1 text-[12px] font-medium text-ds-ink">{t("features:viz.leave.request")}</span>
        <span className="rounded-full bg-ds-pos/15 px-2 py-0.5 text-[11px] font-semibold text-ds-pos">{t("features:viz.leave.approved")}</span>
      </div>
      <div className="mt-3 flex items-center justify-between text-[12px]">
        <span className="text-ds-ink-soft">{t("features:viz.leave.balanceLabel")}</span>
        <span className="font-semibold text-ds-ink">{t("features:viz.leave.balanceValue")}</span>
      </div>
      <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-bg-subtle">
        <m.div className="h-full rounded-full bg-ds-accent" initial={entrance({ width: 0 })} animate={{ width: "60%" }} transition={{ duration: 1, ease: EASE, delay: 0.2 }} />
      </div>
    </div>
  );
}

/* 15 ── Employee documents — files stored & checked off in sequence. */
function DocumentsVisual() {
  const { t } = useLocale();
  const docs = [t("features:viz.docs.contract"), t("features:viz.docs.certificate"), t("features:viz.docs.id")];
  return (
    <div className={`${PANEL} flex h-[120px] flex-col justify-center gap-1.5 p-3`}>
      {docs.map((d, i) => (
        <m.div key={d} initial={entrance({ opacity: 0, x: -6 })} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.18, duration: 0.4, ease: EASE }}
          className="flex items-center gap-2 rounded-lg bg-bg-subtle px-2.5 py-1.5">
          <span className="grid h-5 w-5 place-items-center rounded-md bg-white text-ds-accent"><FileText size={11} /></span>
          <span className="flex-1 text-[11px] font-medium text-ds-ink">{d}</span>
          <span className="grid h-4 w-4 place-items-center rounded-full bg-ds-pos text-white"><Check size={9} strokeWidth={3.5} /></span>
        </m.div>
      ))}
    </div>
  );
}

/* 16 ── Office operations — an activity log / correction trail. */
function ActivityVisual() {
  const { t } = useLocale();
  const log = [{ t: t("features:viz.activity.corrected"), at: "09:12" }, { t: t("features:viz.activity.approved"), at: "10:40" }, { t: t("features:viz.activity.note"), at: "11:05" }];
  return (
    <div className={`${PANEL} grid h-[120px] place-items-center p-3.5`}>
      <div className="flex w-full flex-col gap-2.5 border-l-2 border-line pl-3.5">
        {log.map((e, i) => (
          <m.div key={e.t} initial={entrance({ opacity: 0, x: -6 })} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.18 + i * 0.16, duration: 0.4, ease: EASE }}
            className="relative flex items-center gap-2">
            <span className="absolute -left-[19px] h-2.5 w-2.5 rounded-full bg-ds-accent ring-2 ring-white" />
            <span className="flex-1 text-[11px] font-medium text-ds-ink">{e.t}</span>
            <span className="text-[10px] text-ds-ink-soft">{e.at}</span>
          </m.div>
        ))}
      </div>
    </div>
  );
}

/* 17 ── Internal messaging — chat bubbles + a typing indicator. */
function MessageVisual() {
  const { t } = useLocale();
  // The typing dots are the one loop on this site that never ends, and a
  // stylesheet cannot reach it — motion drives it in JavaScript, so the
  // `prefers-reduced-motion` rules in index.css do not apply. A visitor who has
  // asked for less movement gets the indicator standing still (DIT-553).
  const reduce = useReducedMotion();
  return (
    <div className={`${PANEL} flex h-[120px] flex-col justify-center gap-2 p-3.5`}>
      <m.div initial={entrance({ opacity: 0, y: 6 })} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.4, ease: EASE }}
        className="max-w-[80%] self-start rounded-2xl rounded-bl-sm bg-bg-subtle px-3 py-2 text-[11px] leading-snug text-ds-ink">{t("features:viz.message.incoming")}</m.div>
      <m.div initial={entrance({ opacity: 0, y: 6 })} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.38, duration: 0.4, ease: EASE }}
        className="max-w-[80%] self-end rounded-2xl rounded-br-sm bg-ds-accent px-3 py-2 text-[11px] font-medium text-white">{t("features:viz.message.reply")}</m.div>
      <div className="flex items-center gap-1 self-start rounded-full bg-bg-subtle px-2.5 py-2">
        {[0, 1, 2].map((i) => (
          <m.span key={i} className="h-1.5 w-1.5 rounded-full bg-ds-ink-soft/50"
            animate={reduce ? { opacity: 0.65 } : { opacity: [0.3, 1, 0.3] }}
            transition={reduce ? { duration: 0 } : { duration: 1, repeat: Infinity, delay: i * 0.2, ease: "easeInOut" }} />
        ))}
      </div>
    </div>
  );
}

/* Fallback — a subtle pulsing pin grid. */
function DefaultVisual() {
  return (
    <div className={`${PANEL} h-[120px]`}>
      <div className="absolute inset-0" style={MAP_GRID} />
      <span className="absolute left-1/2 top-1/2 flex h-7 w-7 -translate-x-1/2 -translate-y-1/2">
        <span className="ds-locate absolute inline-flex h-full w-full rounded-full bg-ds-accent" />
        <span className="relative grid h-7 w-7 place-items-center rounded-full bg-ds-accent text-white ring-2 ring-white"><Check size={13} strokeWidth={2.4} /></span>
      </span>
    </div>
  );
}

// Specific categories first; the generic check-in fallback is last.
const VISUALS: Array<{ test: RegExp; Visual: () => JSX.Element }> = [
  { test: /@work|ciao|2027|tamper|audit|legal|watertight|dimona|inspector|\bproof\b|proving presence|\bsync\b/i, Visual: ComplianceVisual },
  { test: /payroll|export|\breport\b|invoice/i, Visual: PayrollVisual },
  { test: /leave request|\bbalance|annual leave|paid leave|time off/i, Visual: LeaveVisual },
  { test: /document|certificate|\bid card|employee file|paperwork/i, Visual: DocumentsVisual },
  { test: /office operation|activity log|\bcorrection|audit trail/i, Visual: ActivityVisual },
  { test: /messag|\bchat\b|inbox|notify team/i, Visual: MessageVisual },
  { test: /absence|sick|\bleave\b|weerverlet|vacation|holiday/i, Visual: AbsenceVisual },
  { test: /overtime/i, Visual: OvertimeVisual },
  { test: /\bbreak\b/i, Visual: BreakVisual },
  { test: /schedul|\bshift|roster/i, Visual: ScheduleVisual },
  { test: /navigat|finding|to site|wayfind/i, Visual: NavigationVisual },
  { test: /mileage|transport|travel|\btrip|carpool|\bkm\b|fleet|logbook/i, Visual: RouteVisual },
  { test: /expense|receipt|extra work|material|photos? (from|on)/i, Visual: ExpenseVisual },
  { test: /subcontractor|crew|\bteam\b|multi-(site|location)|staff|\bpeople\b/i, Visual: TeamVisual },
  { test: /\bcost\b|margin|contract|costing/i, Visual: CostVisual },
  { test: /hour|\btime\b|budget|remaining|countdown|\blive\b/i, Visual: HoursVisual },
  { test: /check.?in|location|gps|geofen|on.?site|\bsite\b|verified|attendance|presence|clock/i, Visual: CheckinVisual },
];

/**
 * Language-independent selection.
 *
 * The regex table above matches ENGLISH words, so on every other locale each
 * title fell through to `DefaultVisual` — twelve languages showed the same empty
 * tile on every card. Callers pass a stable `visual` key instead; the regexes
 * stay only as a fallback for callers that have no key.
 */
const BY_KEY: Record<string, () => JSX.Element> = {
  compliance: ComplianceVisual,
  payroll: PayrollVisual,
  leave: LeaveVisual,
  documents: DocumentsVisual,
  activity: ActivityVisual,
  messaging: MessageVisual,
  absence: AbsenceVisual,
  overtime: OvertimeVisual,
  breaks: BreakVisual,
  schedule: ScheduleVisual,
  navigation: NavigationVisual,
  route: RouteVisual,
  expense: ExpenseVisual,
  team: TeamVisual,
  cost: CostVisual,
  hours: HoursVisual,
  checkin: CheckinVisual,
};

export type FeatureVisualKey = keyof typeof BY_KEY;

/** The mini-visual for a feature — by stable key, else by (English) title. */
export function FeatureVisual({ visual, title }: { visual?: string; title?: string }) {
  const byKey = visual ? BY_KEY[visual] : undefined;
  const byTitle = !byKey && title ? VISUALS.find((v) => v.test.test(title))?.Visual : undefined;
  const Visual = byKey ?? byTitle ?? DefaultVisual;
  return <Visual />;
}
